import { useMemo, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  DateFormatPreferences,
  DEFAULT_PREFERENCES,
  formatDateTimeWithPreferences,
  formatDateWithPreferences,
  formatDateOnlyWithPreferences,
  formatTimeWithPreferences,
  formatRelativeTime,
  formatDuration,
  formatDurationSeconds,
  formatForDateTimeInput,
  formatForDateInput,
} from '../utils/dateFormatter';

/**
 * Hook that provides date formatting functions respecting user preferences
 *
 * Usage:
 *   const { formatDateTime, formatDate, formatTime, formatRelative } = useDateFormatter();
 *
 *   // In JSX:
 *   <span>{formatDateTime(ticket.createdAt)}</span>
 *   <span>{formatDate(ticket.dueDate)}</span>
 *   <span>{formatTime(ticket.scheduledAt)}</span>
 *   <span>{formatRelative(ticket.updatedAt)}</span>
 */
export function useDateFormatter() {
  const { user } = useAuth();

  const preferences: DateFormatPreferences = useMemo(
    () => ({
      timezone: user?.preferences?.timezone || DEFAULT_PREFERENCES.timezone,
      dateFormat: user?.preferences?.dateFormat || DEFAULT_PREFERENCES.dateFormat,
      timeFormat: user?.preferences?.timeFormat || DEFAULT_PREFERENCES.timeFormat,
    }),
    [user?.preferences]
  );

  const formatDateTime = useCallback(
    (date: string | Date | null | undefined, includeSeconds: boolean = false) =>
      formatDateTimeWithPreferences(date, preferences, { includeTime: true, includeSeconds }),
    [preferences]
  );

  const formatDate = useCallback(
    (date: string | Date | null | undefined) => formatDateWithPreferences(date, preferences),
    [preferences]
  );

  /** Use for date-only fields (e.g. workedDate) so the same date shows in list, edit, and modals. */
  const formatDateOnly = useCallback(
    (date: string | Date | null | undefined) => formatDateOnlyWithPreferences(date, preferences),
    [preferences]
  );

  const formatTime = useCallback(
    (date: string | Date | null | undefined, includeSeconds: boolean = false) =>
      formatTimeWithPreferences(date, preferences, includeSeconds),
    [preferences]
  );

  const formatRelative = useCallback(
    (date: string | Date | null | undefined) => formatRelativeTime(date, preferences),
    [preferences]
  );

  const formatDateTimeForInput = useCallback(
    (date: string | Date | null | undefined) =>
      formatForDateTimeInput(date, preferences.timezone),
    [preferences.timezone]
  );

  const formatDateForInput = useCallback(
    (date: string | Date | null | undefined) => formatForDateInput(date, preferences.timezone),
    [preferences.timezone]
  );

  // Get today's date in user's timezone (YYYY-MM-DD format)
  const getToday = useCallback(
    () => formatForDateInput(new Date(), preferences.timezone),
    [preferences.timezone]
  );

  return {
    preferences,
    formatDateTime,
    formatDate,
    formatDateOnly,
    formatTime,
    formatRelative,
    formatDuration, // Static function, no preferences needed
    formatDurationSeconds, // Static function, no preferences needed
    formatDateTimeForInput,
    formatDateForInput,
    getToday,
  };
}

export default useDateFormatter;
